"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { ArrowRight, ChartBar, FileText, Gavel, Globe, Landmark, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Separator } from "@/components/ui/separator";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  id: "policy-2021",
  year: "2021",
  title: "Small Business Relief Fund",
  category: "Economic Recovery",
  icon: <Landmark className="size-5" />,
  shortDesc: "Secured $5M in emergency grants.",
  fullDesc: "In the wake of the pandemic, COHCC led a coalition to secure $5 million in direct emergency grants specifically targeted at minority-owned small businesses in Colorado that were excluded from initial federal relief rounds.",
  metrics: [{
    label: "Funds Secured",
    value: "$5.0M"
  }, {
    label: "Businesses Saved",
    value: "350+"
  }, {
    label: "Jobs Retained",
    value: "1,200"
  }, {
    label: "County Coverage",
    value: "12"
  }],
  status: "Implemented"
}, {
  id: "policy-2022",
  year: "2022",
  title: "Procurement Equity Act",
  category: "Legislative Win",
  icon: <Gavel className="size-5" />,
  shortDesc: "Mandated 15% minority contracting.",
  fullDesc: "Successfully lobbied for state-level legislation requiring a minimum of 15% of state procurement contracts to be awarded to minority- and women-owned business enterprises (MWBEs), opening new revenue streams for Hispanic contractors.",
  metrics: [{
    label: "Contract Value",
    value: "$40B+"
  }, {
    label: "Policy Target",
    value: "15%"
  }, {
    label: "New Vendors",
    value: "85"
  }, {
    label: "Legislative Vote",
    value: "58-7"
  }],
  status: "Enacted"
}, {
  id: "policy-2023",
  year: "2023",
  title: "Workforce Innovation Grant",
  category: "Education & Labor",
  icon: <Users className="size-5" />,
  shortDesc: "Launched bilingual tech training.",
  fullDesc: "Partnered with the Department of Labor to launch a first-of-its-kind bilingual technology apprenticeship program, bridging the digital divide and placing Hispanic talent in high-growth tech sectors.",
  metrics: [{
    label: "Students Trained",
    value: "500+"
  }, {
    label: "Placement Rate",
    value: "82%"
  }, {
    label: "Avg Salary Inc",
    value: "+$18k"
  }, {
    label: "Corp Partners",
    value: "24"
  }],
  status: "Active"
}, {
  id: "policy-2024",
  year: "2024",
  title: "Hispanic Business Tax Credit",
  category: "Fiscal Policy",
  icon: <TrendingUp className="size-5" />,
  shortDesc: "Incentives for community investment.",
  fullDesc: "Advocated for a tiered tax credit system for businesses that invest in designated Hispanic Cultural Districts, fostering local development and preserving cultural heritage sites across Denver and Pueblo.",
  metrics: [{
    label: "Tax Credit Cap",
    value: "$2M/yr"
  }, {
    label: "Districts",
    value: "4"
  }, {
    label: "Proj. Investment",
    value: "$12M"
  }, {
    label: "Biz Support",
    value: "94%"
  }],
  status: "Passed"
}, {
  id: "policy-2025",
  year: "2025",
  title: "Future Economy Initiative",
  category: "Strategic Vision",
  icon: <Globe className="size-5" />,
  shortDesc: "AI & Green Energy transition roadmap.",
  fullDesc: "Launching a comprehensive 5-year roadmap to position Hispanic-owned businesses at the forefront of the AI and Green Energy revolutions, ensuring equitable access to the next wave of economic prosperity.",
  metrics: [{
    label: "Research Budget",
    value: "$1.5M"
  }, {
    label: "Sector Focus",
    value: "AI/Green"
  }, {
    label: "Stakeholders",
    value: "150+"
  }, {
    label: "Launch Date",
    value: "Q3 2025"
  }],
  status: "In Progress"
}];
export default function AdvocacyInAction() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="advocacy-in-action" ref={sectionRef} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-primary" />
            <span className="text-primary font-mono text-sm tracking-widest uppercase">
              Policy & Advocacy
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Advocacy in <span className="text-primary">Action</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed max-w-2xl">
            Tracking our legislative wins and strategic initiatives. We leverage data to drive policy changes that empower the 120,000+ Hispanic-owned businesses in Colorado.
          </p>
        </div>

        {}
        <div className="relative w-full min-h-[400px] flex flex-col justify-center">
          
          {}
          <div className="absolute top-1/2 left-0 w-full -translate-y-1/2 hidden md:block">
            <Separator className="bg-border/40 h-[2px]" />
          </div>

          {}
          <ScrollArea className="w-full whitespace-nowrap pb-12 pt-12">
            <div className="flex md:flex-row flex-col md:items-center gap-8 md:gap-0 min-w-full md:px-12">
              
              {milestones.map((item, i) => <div key={item.id} data-index={i} className="relative flex-shrink-0 md:w-[300px] group">
                  {}
                  <div className="absolute top-1/2 left-0 w-full h-[2px] bg-border/40 hidden md:block -z-10" />
                  
                  {}
                  <SafeHydrate><Drawer>
                    <DrawerTrigger asChild>
                      <div className="flex flex-col items-start md:items-center cursor-pointer group/node">
                        
                        {}
                        <div className="relative z-10 mb-4 md:mb-0 transition-transform duration-300 group-hover/node:scale-110">
                          <div className="size-4 md:size-6 rounded-full bg-background border-2 border-primary ring-4 ring-background group-hover/node:ring-primary/20 transition-all shadow-[0_0_15px_rgba(var(--primary),0.5)]" />
                          {}
                          <div className={`absolute left-1/2 -translate-x-1/2 w-[2px] h-8 bg-border/60 transition-all duration-300 group-hover/node:bg-primary/60 ${i % 2 === 0 ? 'bottom-full mb-2' : 'top-full mt-2'} hidden md:block`} />
                        </div>

                        {}
                        <div className={`
                          md:absolute md:left-1/2 md:-translate-x-1/2 w-full md:w-64 
                          p-5 rounded-lg border border-border/50 bg-card/50 backdrop-blur-sm 
                          hover:bg-card hover:border-primary/30 transition-all duration-300
                          ${i % 2 === 0 ? 'md:bottom-[calc(50%+40px)]' : 'md:top-[calc(50%+40px)]'}
                        `}>
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-primary font-mono text-xs font-bold px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                              {item.year}
                            </span>
                            <div className="text-muted-foreground group-hover/node:text-primary transition-colors">
                              {item.icon}
                            </div>
                          </div>
                          <h3 className="text-lg font-semibold text-foreground mb-1 group-hover/node:text-primary transition-colors whitespace-normal">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 whitespace-normal">
                            {item.shortDesc}
                          </p>
                          <div className="mt-3 flex items-center text-xs text-primary font-medium opacity-0 -translate-x-2 group-hover/node:opacity-100 group-hover/node:translate-x-0 transition-all duration-300">
                            View Impact Data <ArrowRight className="ml-1 size-3" />
                          </div>
                        </div>

                      </div>
                    </DrawerTrigger>
                    
                    {}
                    <DrawerContent className="dark bg-background border-t border-border/50 max-h-[85vh]">
                      <div className="mx-auto w-full max-w-4xl">
                        <DrawerHeader className="border-b border-border/40 pb-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-primary font-mono text-sm font-bold px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                                  {item.year}
                                </span>
                                <span className="text-muted-foreground text-sm font-mono uppercase tracking-wider">
                                  {item.category}
                                </span>
                              </div>
                              <DrawerTitle className="text-3xl md:text-4xl font-bold text-foreground">
                                {item.title}
                              </DrawerTitle>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`px-3 py-1 rounded-full text-xs font-bold border ${item.status === 'Enacted' || item.status === 'Passed' ? 'bg-green-500/10 text-green-500 border-green-500/20' : item.status === 'Active' || item.status === 'Implemented' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'}`}>
                                    STATUS: {item.status.toUpperCase()}
                                </div>
                            </div>
                          </div>
                          <DrawerDescription className="mt-4 text-lg text-muted-foreground max-w-2xl">
                            {item.fullDesc}
                          </DrawerDescription>
                        </DrawerHeader>

                        <div className="p-6 md:p-8">
                          <h4 className="text-sm font-mono text-muted-foreground uppercase tracking-widest mb-6 flex items-center gap-2">
                            <ChartBar className="size-4" /> Impact Metrics
                          </h4>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
                            {item.metrics.map((metric, idx) => <div key={idx} className="bg-card/30 p-4 rounded border border-border/40">
                                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                                  {metric.value}
                                </div>
                                <div className="text-xs md:text-sm text-muted-foreground font-medium">
                                  {metric.label}
                                </div>
                              </div>)}
                          </div>

                          <div className="mt-8 p-4 bg-primary/5 border border-primary/10 rounded-lg flex items-start gap-3">
                            <FileText className="size-5 text-primary mt-0.5 shrink-0" />
                            <div>
                              <h5 className="text-sm font-bold text-foreground mb-1">Research & Documentation</h5>
                              <p className="text-xs text-muted-foreground mb-3">
                                Access the full legislative text, white papers, and economic impact analysis reports associated with this initiative.
                              </p>
                              <Button variant="outline" size="sm" className="h-8 text-xs border-primary/30 hover:bg-primary/10 hover:text-primary">
                                Download Report PDF
                              </Button>
                            </div>
                          </div>
                        </div>

                        <DrawerFooter className="border-t border-border/40 pt-4">
                          <div className="flex justify-between items-center w-full">
                            <p className="text-xs text-muted-foreground font-mono">
                              COHCC POLICY TRACKER ID: {item.id.toUpperCase()}
                            </p>
                            <DrawerClose asChild>
                              <Button variant="ghost">Close Details</Button>
                            </DrawerClose>
                          </div>
                        </DrawerFooter>
                      </div>
                    </DrawerContent>
                  </Drawer></SafeHydrate>
                </div>)}
              
              {}
              <div className="w-12 shrink-0" />
            </div>
            <ScrollBar orientation="horizontal" className="bg-border/20" />
          </ScrollArea>
        </div>

        {}
        <div className="mt-12 flex flex-col md:flex-row justify-between items-center gap-6 border-t border-border/30 pt-8">
          <div className="flex items-center gap-4">
            <div className="flex -space-x-3">
              {[1, 2, 3].map(i => <div key={i} className="size-10 rounded-full border-2 border-background bg-muted flex items-center justify-center overflow-hidden">
                   <img src={`/img/avatar_advocacy_${i}.png`} alt="Advocate" className="w-full h-full object-cover" />
                </div>)}
            </div>
            <div className="text-sm">
              <p className="text-foreground font-medium">Join 500+ Advocates</p>
              <p className="text-muted-foreground text-xs">Help shape the future of Colorado.</p>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Link to="/JoinUsPage">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                Join the Chamber
              </Button>
            </Link>
            <Link to="/IntelligencePage">
              <Button variant="outline" className="border-border hover:bg-accent hover:text-accent-foreground">
                View All Research
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}